/**
 * @fileoverview Outside-click/Escape dismissal hook for dropdowns and popovers.
 * @description Returns a ref to attach to the dropdown's outer element; while `open` is true, a
 * click outside that element or an Escape keypress calls `onClose`. No domain coupling — any
 * dropdown/popover component can reuse it (see DashboardHeaderUserMenu and
 * DashboardHeaderNotifications for the pattern this generalizes).
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { useEffect, useRef } from 'react'

import type { RefObject } from 'react'

/**
 * @description Closes an open dropdown/popover on an outside click or Escape.
 * @param {boolean} open - Whether the dropdown is currently open; the listeners are only attached
 * while true.
 * @param {() => void} onClose - Called on an outside click or Escape.
 * @returns {RefObject<T | null>} Attach to the dropdown's outer (positioning) element.
 */
export function useOutsideClick<T extends HTMLElement>(
  open: boolean,
  onClose: () => void,
): RefObject<T | null> {
  const ref = useRef<T>(null)

  useEffect(() => {
    if (!open) {
      return
    }

    /**
     * @description Closes the dropdown on a click outside it or on Escape.
     * @param {MouseEvent | KeyboardEvent} event - The document pointerdown/keydown event.
     */
    const handleOutsideInteraction = (event: MouseEvent | KeyboardEvent) => {
      if (event instanceof KeyboardEvent) {
        if (event.key === 'Escape') {
          onClose()
        }
        return
      }

      if (ref.current && !ref.current.contains(event.target as Node)) {
        onClose()
      }
    }

    document.addEventListener('mousedown', handleOutsideInteraction)
    document.addEventListener('keydown', handleOutsideInteraction)
    return () => {
      document.removeEventListener('mousedown', handleOutsideInteraction)
      document.removeEventListener('keydown', handleOutsideInteraction)
    }
  }, [open, onClose])

  return ref
}
